/**
 * ShortlistPage.tsx — clean scratch build 27 Apr 2026
 *
 * Tap photo → active (white border)
 * Tap bin  → toggles on/off (highlights)
 * Tap photo again → saves, deactivates
 * Tap different photo → saves current, activates new
 */

import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Spinner } from '@/components/ui'
import { fetchBins } from '@/lib/events'
import type { Venue } from '@/types'
import type { PickerSession } from '@/lib/venue'

interface Props { venue: Venue; picker: PickerSession }

// ── Types ─────────────────────────────────────────────────────────────────────

interface Pick {
  id:            string
  ai_score:      number | null
  picker_action: string | null
  media: { cloudinary_url: string; ai_description: string | null } | null
  assignments:   { id: string; lane: string }[]
}

// ── Data ──────────────────────────────────────────────────────────────────────

async function loadPicks(venueId: string): Promise<Pick[]> {
  const { data, error } = await supabase
    .from('venue_picks')
    .select(`id, ai_score, picker_action,
      media:media_items(cloudinary_url, ai_description),
      assignments:photo_assignments(id, lane)`)
    .eq('venue_id', venueId)
    .in('picker_action', ['saved', 'needs_approval', 'selected'])
    .order('ai_score', { ascending: false })
  if (error) throw error
  return (data ?? []) as unknown as Pick[]
}

async function saveLanes(pickId: string, venueId: string, lanes: string[]) {
  // Delete ALL existing assignments for this pick then re-insert clean
  await supabase.from('photo_assignments').delete().eq('pick_id', pickId)
  
  for (const l of lanes) {
    await supabase.from('photo_assignments').insert({
      pick_id: pickId, venue_id: venueId, lane: l, status: 'pending'
    })
  }
}

// ── Colour helper ─────────────────────────────────────────────────────────────

function rgba(hex: string, a: number) {
  if (!hex || hex.length < 7) return `rgba(96,165,250,${a})`
  const r = parseInt(hex.slice(1,3), 16)
  const g = parseInt(hex.slice(3,5), 16)
  const b = parseInt(hex.slice(5,7), 16)
  return `rgba(${r},${g},${b},${a})`
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ShortlistPage({ venue }: Props) {
  // Which pick is currently active
  const [activeId, setActiveId]       = useState<string | null>(null)
  // Which lanes are toggled for the active pick — use ref for reliable async access
  const activeLanesRef                = useRef<string[]>([])
  const [activeLanes, setActiveLanes] = useState<string[]>([])
  // Status message
  const [status, setStatus]           = useState('')

  // Load picks
  const { data: picks = [], isLoading, refetch } = useQuery({
    queryKey: ['shortlist', venue.id],
    queryFn:  () => loadPicks(venue.id),
    staleTime: 0,
  })

  // Load bins from DB
  const { data: bins = [] } = useQuery({
    queryKey: ['bins', venue.id],
    queryFn:  () => fetchBins(venue.id),
    staleTime: 10 * 60_000,
  })

  // ── Handlers ────────────────────────────────────────────────────────────────

  async function handlePickTap(pick: Pick) {
    if (activeId === pick.id) {
      // Second tap on same photo — save and deactivate
      const lanes = activeLanesRef.current
      setStatus(`Saving [${lanes.join(', ') || 'none'}]…`)
      setActiveId(null)
      setActiveLanes([])
      activeLanesRef.current = []
      try {
        await saveLanes(pick.id, venue.id, lanes)
        setStatus('Saved ✓')
      } catch (e) {
        setStatus(`Error: ${e instanceof Error ? e.message : 'unknown'}`)
      }
      setTimeout(() => setStatus(''), 3000)
      refetch()
    } else {
      // First tap or switching photo — save previous if any, then activate new
      if (activeId) {
        const lanes = activeLanesRef.current
        const prev  = picks.find(p => p.id === activeId)
        if (prev) await saveLanes(prev.id, venue.id, lanes)
        refetch()
      }
      // Seed with existing assignments
      const existing = pick.assignments.map(a => a.lane)
      activeLanesRef.current = existing
      setActiveLanes(existing)
      setActiveId(pick.id)
      setStatus('')
    }
  }

  function handleBinTap(binName: string) {
    if (!activeId) return
    const current = activeLanesRef.current
    const next = current.includes(binName)
      ? current.filter(l => l !== binName)
      : [...current, binName]
    activeLanesRef.current = next
    setActiveLanes(next)
  }

  // ── Bin counts (from persisted data) ──────────────────────────────────────

  const binCounts: Record<string, number> = {}
  for (const bin of bins) {
    binCounts[bin.name] = picks.filter(p =>
      p.assignments.some(a => a.lane === bin.name)
    ).length
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner size={24} /></div>

  if (!picks.length) return (
    <div style={{ textAlign: 'center', padding: '80px 24px', color: 'rgba(255,255,255,0.4)' }}>
      <p style={{ fontWeight: 600, color: 'white', margin: '0 0 8px' }}>No photos shortlisted yet</p>
      <p style={{ fontSize: 13, margin: 0 }}>Switch to Picker role and start picking</p>
    </div>
  )

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

      {/* ── Grid ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ color: 'white', fontWeight: 700, fontSize: 16 }}>
            Shortlist · {picks.length}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
            {activeId ? 'Tap bins → tap photo to save' : 'Tap a photo to assign'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 5 }}>
          {picks.map((pick, idx) => {
            const isActive  = activeId === pick.id
            const url       = pick.media?.cloudinary_url?.replace('/upload/', '/upload/w_300,c_fill,q_auto,f_auto/')
            // Show live lanes if active, else persisted
            const dots = isActive ? activeLanes : pick.assignments.map(a => a.lane)

            return (
              <div key={pick.id} onClick={() => handlePickTap(pick)}
                style={{
                  position: 'relative', aspectRatio: '1', borderRadius: 10,
                  overflow: 'hidden', background: 'rgba(255,255,255,0.05)',
                  cursor: 'pointer',
                  border: isActive ? '3px solid white' : '3px solid transparent',
                  transform: isActive ? 'scale(1.04)' : 'scale(1)',
                  transition: 'transform 0.12s, border-color 0.12s',
                }}>
                {url && <img src={url} alt="" loading="lazy" draggable={false}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}

                {/* Score */}
                {pick.ai_score != null && (
                  <div style={{ position: 'absolute', top: 4, right: 4,
                    background: 'rgba(0,0,0,0.75)', borderRadius: 999,
                    padding: '1px 5px', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
                    {Math.round(pick.ai_score * 100)}
                  </div>
                )}

                {/* Active number */}
                {isActive && (
                  <div style={{ position: 'absolute', top: 4, left: 4,
                    background: 'white', borderRadius: 999,
                    padding: '1px 6px', fontSize: 9, fontWeight: 800, color: 'black' }}>
                    #{idx + 1}
                  </div>
                )}

                {/* Lane dots */}
                {dots.length > 0 && (
                  <div style={{ position: 'absolute', bottom: 4, left: 4, display: 'flex', gap: 3 }}>
                    {dots.map(lane => {
                      const bin = bins.find(b => b.name === lane)
                      const col = bin?.color ?? '#60a5fa'
                      return <div key={lane} style={{ width: 7, height: 7, borderRadius: '50%', background: col, boxShadow: `0 0 4px ${col}` }} />
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Bins panel ── */}
      <div style={{ width: 160, flexShrink: 0, position: 'sticky', top: 16 }}>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>
          Bins
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {bins.map(bin => {
            const on    = activeLanes.includes(bin.name)
            const count = binCounts[bin.name] ?? 0

            return (
              <button key={bin.id} type="button"
                onClick={() => handleBinTap(bin.name)}
                disabled={!activeId}
                style={{
                  width: '100%', padding: '11px 13px', borderRadius: 12,
                  border: `2.5px solid ${on ? bin.color : rgba(bin.color, 0.45)}`,
                  background: on ? rgba(bin.color, 0.28) : rgba(bin.color, 0.08),
                  display: 'flex', alignItems: 'center', gap: 9,
                  cursor: activeId ? 'pointer' : 'not-allowed',
                  opacity: activeId ? 1 : 0.6,
                  transition: 'all 0.1s',
                  transform: on ? 'scale(1.03)' : 'scale(1)',
                }}>
                <div style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  background: bin.color,
                  boxShadow: on ? `0 0 8px ${bin.color}` : 'none' }} />
                <span style={{ flex: 1, textAlign: 'left', fontWeight: on ? 700 : 600,
                  fontSize: 13, color: on ? 'white' : rgba(bin.color, 0.85) }}>
                  {bin.name}
                </span>
                {count > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 800, color: bin.color,
                    background: rgba(bin.color, 0.15), padding: '1px 6px', borderRadius: 999 }}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Summary */}
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 10,
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: 9, margin: '0 0 2px' }}>Assigned</p>
          <p style={{ color: 'white', fontWeight: 700, fontSize: 16, margin: 0 }}>
            {picks.filter(p => p.assignments.length > 0).length}
            <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 400, fontSize: 11 }}> of {picks.length}</span>
          </p>
        </div>

        {/* Status */}
        {status && (
          <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8,
            background: status.includes('✓') ? 'rgba(52,211,153,0.15)' : 'rgba(96,165,250,0.15)',
            border: `1px solid ${status.includes('✓') ? 'rgba(52,211,153,0.4)' : 'rgba(96,165,250,0.4)'}` }}>
            <p style={{ color: status.includes('✓') ? '#34d399' : '#60a5fa', fontSize: 10, margin: 0 }}>
              {status}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

